import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Upload, Image as ImageIcon, Film, Plus, Folder, FolderPlus, Share2, MoreHorizontal, Star, Trash2, Edit3 } from 'lucide-react';
import { api, mediaUrl, thumbUrl } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { MasonryGrid } from '@/components/MasonryGrid';
import { MediaLightbox } from '@/components/MediaLightbox';
import { StorageWidget, formatBytes } from '@/components/StorageWidget';
import { ShareLinkDialog } from '@/components/ShareLinkDialog';

export default function MediaTab({ event, user }) {
  const [items, setItems] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [albumFilter, setAlbumFilter] = useState('all'); // 'all' | 'none' | album.id
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const [createAlbum, setCreateAlbum] = useState(false);
  const [albumForm, setAlbumForm] = useState({ title: '', description: '' });
  const [shareOpen, setShareOpen] = useState(null); // { type, target_id, title }
  const [captionEdit, setCaptionEdit] = useState(null); // { id, caption, album_id }
  const fileRef = useRef(null);

  const isOrganizer = event.owner_id === user?.id || user?.is_admin;

  const load = useCallback(async () => {
    try {
      const q = albumFilter && albumFilter !== 'all' ? `?album_id=${encodeURIComponent(albumFilter)}` : '';
      const [m, a] = await Promise.all([
        api.get(`/events/${event.id}/media${q}`),
        api.get(`/events/${event.id}/albums`),
      ]);
      setItems(m.data || []);
      setAlbums(a.data || []);
    } catch (err) {
      console.error('Failed to load media:', err);
    }
  }, [event.id, albumFilter]);
  const loadParticipants = useCallback(async () => {
    try {
      const r = await api.get(`/events/${event.id}/participants`);
      setParticipants(r.data || []);
    } catch (err) {
      console.error('Failed to load participants:', err);
    }
  }, [event.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadParticipants(); }, [loadParticipants]);

  const onPick = () => fileRef.current?.click();

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });
    let okCount = 0;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const fd = new FormData();
      fd.append('file', f);
      fd.append('caption', '');
      if (albumFilter !== 'all' && albumFilter !== 'none') fd.append('album_id', albumFilter);
      try {
        await api.post(`/events/${event.id}/media`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        okCount++;
      } catch (err) {
        toast.error(`${f.name}: ${err?.response?.data?.detail || 'failed'}`);
      } finally {
        setUploadProgress({ done: i + 1, total: files.length });
      }
    }
    setUploading(false);
    if (okCount) toast.success(`${okCount} uploaded`);
    load();
    if (fileRef.current) fileRef.current.value = '';
  };

  const remove = async (m) => {
    if (!window.confirm('Remove this media?')) return;
    try { await api.delete(`/media/${m.id}`); setItems((arr) => arr.filter((x) => x.id !== m.id)); toast.success('Removed'); }
    catch (e) { toast.error('Failed'); }
  };

  const createAlbumSubmit = async () => {
    if (!albumForm.title.trim()) return toast.error('Title required');
    try {
      const r = await api.post(`/events/${event.id}/albums`, albumForm);
      setAlbums((arr) => [r.data, ...arr]);
      setCreateAlbum(false);
      setAlbumForm({ title: '', description: '' });
      setAlbumFilter(r.data.id);
      toast.success('Album created');
    } catch (e) { toast.error('Failed'); }
  };

  const setCover = async (albumId, mediaId) => {
    try { await api.put(`/albums/${albumId}`, { cover_media_id: mediaId }); toast.success('Cover set'); load(); }
    catch (e) { toast.error('Failed'); }
  };
  const deleteAlbum = async (a) => {
    if (!window.confirm(`Delete album "${a.title}"? Media will stay.`)) return;
    try { await api.delete(`/albums/${a.id}`); setAlbums((arr) => arr.filter((x) => x.id !== a.id)); setAlbumFilter('all'); toast.success('Album deleted'); }
    catch (e) { toast.error('Failed'); }
  };

  const submitCaption = async () => {
    if (!captionEdit) return;
    try {
      const r = await api.put(`/media/${captionEdit.id}`, { caption: captionEdit.caption, album_id: captionEdit.album_id || null });
      setItems((arr) => arr.map((m) => m.id === r.data.id ? { ...m, ...r.data } : m));
      setCaptionEdit(null);
      toast.success('Updated');
    } catch (e) { toast.error('Failed'); }
  };

  const visibleItems = items;

  return (
    <div className="space-y-3" data-testid="media-tab">
      {/* Storage widget */}
      <StorageWidget scope="event" eventId={event.id} testid="event-storage-widget" />

      {/* Album chips */}
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1" data-testid="album-chips">
        <AlbumChip active={albumFilter === 'all'} onClick={() => setAlbumFilter('all')} testid="album-chip-all">
          <ImageIcon className="h-3.5 w-3.5" />All
          <span className="text-[10px] opacity-70 ml-1">{items.length}</span>
        </AlbumChip>
        <AlbumChip active={albumFilter === 'none'} onClick={() => setAlbumFilter('none')} testid="album-chip-none">
          Unsorted
        </AlbumChip>
        {albums.map((a) => (
          <AlbumChip key={a.id} active={albumFilter === a.id} onClick={() => setAlbumFilter(a.id)} testid={`album-chip-${a.id}`}>
            <Folder className="h-3.5 w-3.5" />{a.title}
            <span className="text-[10px] opacity-70 ml-1">{a.media_count}</span>
          </AlbumChip>
        ))}
        <button data-testid="create-album-button" onClick={() => setCreateAlbum(true)} className="shrink-0 px-3 py-1.5 rounded-full border border-dashed border-border text-xs font-medium text-muted-foreground hover:bg-secondary flex items-center gap-1.5"><FolderPlus className="h-3.5 w-3.5" />New album</button>
      </div>

      {/* Album header actions when album selected */}
      {albumFilter !== 'all' && albumFilter !== 'none' && (
        <Card className="rounded-2xl p-3 flex items-center justify-between" data-testid="album-toolbar">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Album</div>
            <div className="font-serif text-lg font-semibold truncate">{albums.find((a) => a.id === albumFilter)?.title}</div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => setShareOpen({ type: 'album', target_id: albumFilter, title: 'album' })} data-testid="album-share-button" className="rounded-xl text-xs"><Share2 className="h-3.5 w-3.5 mr-1" />Share</Button>
            {isOrganizer && (
              <Button variant="ghost" size="icon" onClick={() => deleteAlbum(albums.find((a) => a.id === albumFilter))} data-testid="album-delete-button" className="rounded-xl text-destructive"><Trash2 className="h-4 w-4" /></Button>
            )}
          </div>
        </Card>
      )}

      {/* Upload + Share event */}
      <Card className="rounded-2xl p-3 flex items-center justify-between gap-2">
        <div className="text-sm text-muted-foreground">{visibleItems.length} item{visibleItems.length === 1 ? '' : 's'}</div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShareOpen({ type: 'event', target_id: event.id, title: 'event gallery' })} data-testid="event-share-button" className="rounded-xl text-xs"><Share2 className="h-3.5 w-3.5 mr-1" />Share gallery</Button>
          <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={upload} data-testid="media-file-input" />
          <Button onClick={onPick} disabled={uploading} data-testid="media-upload-button" className="rounded-xl gradient-fab text-white">
            <Upload className="h-4 w-4 mr-2" />
            {uploading ? `Uploading ${uploadProgress.done}/${uploadProgress.total}` : 'Upload'}
          </Button>
        </div>
      </Card>

      {/* Grid */}
      {visibleItems.length === 0 ? (
        <Card className="rounded-2xl p-6 text-center text-sm text-muted-foreground">No photos or videos yet.</Card>
      ) : (
        <MasonryGrid testid="media-masonry">
          {visibleItems.map((m, idx) => (
            <MediaTile
              key={m.id}
              m={m}
              idx={idx}
              isOwnerOrAdmin={isOrganizer || m.uploader_id === user?.id}
              onOpen={() => setLightboxIdx(idx)}
              onSetCover={(albumId) => setCover(albumId, m.id)}
              albums={albums}
              onEditCaption={() => setCaptionEdit({ id: m.id, caption: m.caption || '', album_id: m.album_id || '' })}
              onShare={() => setShareOpen({ type: 'media', target_id: m.id, title: 'photo' })}
              onDelete={() => remove(m)}
            />
          ))}
        </MasonryGrid>
      )}

      {/* Lightbox */}
      <MediaLightbox
        open={lightboxIdx >= 0}
        indexInitial={lightboxIdx}
        items={items}
        onClose={() => setLightboxIdx(-1)}
        onUpdated={(updated, i) => setItems((arr) => arr.map((x, j) => j === i ? updated : x))}
        participants={participants}
      />

      {/* Create album dialog */}
      <Dialog open={createAlbum} onOpenChange={setCreateAlbum}>
        <DialogContent className="rounded-2xl max-w-sm" data-testid="album-create-dialog">
          <DialogHeader><DialogTitle className="font-serif">New album</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Title</Label><Input value={albumForm.title} onChange={(e) => setAlbumForm((f) => ({ ...f, title: e.target.value }))} placeholder="Day 1, Sunset shots, Highlights" data-testid="album-title-input" /></div>
            <div><Label className="text-xs">Description (optional)</Label><Textarea rows={2} value={albumForm.description} onChange={(e) => setAlbumForm((f) => ({ ...f, description: e.target.value }))} data-testid="album-desc-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateAlbum(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={createAlbumSubmit} data-testid="album-create-submit" className="rounded-xl gradient-fab text-white">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit caption dialog */}
      <Dialog open={!!captionEdit} onOpenChange={(v) => !v && setCaptionEdit(null)}>
        <DialogContent className="rounded-2xl max-w-sm" data-testid="caption-dialog">
          <DialogHeader><DialogTitle className="font-serif">Edit details</DialogTitle></DialogHeader>
          {captionEdit && (
            <div className="space-y-3">
              <div><Label className="text-xs">Caption</Label><Textarea rows={3} value={captionEdit.caption} onChange={(e) => setCaptionEdit((c) => ({ ...c, caption: e.target.value }))} data-testid="caption-input" /></div>
              <div>
                <Label className="text-xs">Album</Label>
                <select value={captionEdit.album_id || ''} onChange={(e) => setCaptionEdit((c) => ({ ...c, album_id: e.target.value }))} data-testid="album-assign-select" className="w-full h-10 px-3 rounded-xl border border-input bg-card text-sm">
                  <option value="">— Unsorted —</option>
                  {albums.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
                </select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCaptionEdit(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={submitCaption} data-testid="caption-save" className="rounded-xl gradient-fab text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share dialog */}
      {shareOpen && (
        <ShareLinkDialog
          open={!!shareOpen}
          onOpenChange={(v) => !v && setShareOpen(null)}
          type={shareOpen.type}
          targetId={shareOpen.target_id}
          title={shareOpen.title}
        />
      )}
    </div>
  );
}

const AlbumChip = ({ children, active, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border transition-colors ${active ? 'bg-foreground text-background border-foreground' : 'bg-card border-border text-muted-foreground hover:bg-secondary'}`}>
    {children}
  </button>
);

const MediaTile = ({ m, idx, isOwnerOrAdmin, onOpen, onSetCover, albums, onEditCaption, onShare, onDelete }) => {
  const [menu, setMenu] = useState(false);
  const aspect = (m.width && m.height) ? (m.width / m.height) : 1;
  // Cap aspect to avoid super-tall items
  const ratio = Math.max(0.4, Math.min(2.0, aspect));
  // Render: image uses thumb url; video uses original
  return (
    <div className="relative group rounded-xl overflow-hidden bg-secondary" data-testid={`media-${m.id}`}>
      <div style={{ aspectRatio: `${ratio}` }} className="w-full">
        {m.is_video ? (
          <video
            src={mediaUrl(m.id)}
            preload="metadata"
            className="w-full h-full object-cover"
          />
        ) : (
          <img
            loading="lazy"
            src={thumbUrl(m.id)}
            alt={m.caption || ''}
            className="w-full h-full object-cover"
          />
        )}
      </div>
      <button onClick={onOpen} className="absolute inset-0" aria-label="Open" data-testid={`media-open-${m.id}`} />
      <div className="absolute top-1.5 left-1.5 flex items-center gap-1">
        {m.is_video ? <Film className="h-3.5 w-3.5 text-white drop-shadow" /> : null}
        {m.favorited && <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400 drop-shadow" />}
      </div>
      {/* Reactions badge */}
      {m.reactions && Object.keys(m.reactions).length > 0 && (
        <div className="absolute bottom-1.5 left-1.5 bg-black/55 text-white text-[10px] rounded-full px-2 py-0.5 flex items-center gap-1">
          {Object.entries(m.reactions).slice(0, 3).map(([e, c]) => <span key={e}>{e}{c}</span>)}
        </div>
      )}
      {/* Caption pill */}
      {m.caption && (
        <div className="absolute bottom-1.5 right-1.5 max-w-[60%] truncate bg-black/55 text-white text-[10px] rounded-full px-2 py-0.5">{m.caption}</div>
      )}
      {/* Top-right menu */}
      <div className="absolute top-1 right-1">
        <button onClick={(e) => { e.stopPropagation(); setMenu((v) => !v); }} className="h-7 w-7 rounded-md bg-black/55 text-white flex items-center justify-center" data-testid={`media-menu-${m.id}`}><MoreHorizontal className="h-4 w-4" /></button>
        {menu && (
          <div className="absolute top-full right-0 mt-1 w-44 bg-card border rounded-xl shadow-lg overflow-hidden z-10 text-left" onClick={(e) => e.stopPropagation()}>
            <MenuItem testid={`media-edit-${m.id}`} onClick={() => { setMenu(false); onEditCaption(); }}><Edit3 className="h-3.5 w-3.5 mr-2" />Edit caption / album</MenuItem>
            <MenuItem testid={`media-share-${m.id}`} onClick={() => { setMenu(false); onShare(); }}><Share2 className="h-3.5 w-3.5 mr-2" />Share photo</MenuItem>
            {albums.length > 0 && (
              <div className="px-3 py-1.5 text-[11px] uppercase text-muted-foreground border-t">Set as album cover</div>
            )}
            {albums.map((a) => (
              <MenuItem key={a.id} testid={`media-cover-${m.id}-${a.id}`} onClick={() => { setMenu(false); onSetCover(a.id); }}>
                <Folder className="h-3.5 w-3.5 mr-2" />{a.title}
              </MenuItem>
            ))}
            {isOwnerOrAdmin && <MenuItem testid={`media-delete-${m.id}`} onClick={() => { setMenu(false); onDelete(); }} className="text-destructive"><Trash2 className="h-3.5 w-3.5 mr-2" />Delete</MenuItem>}
          </div>
        )}
      </div>
    </div>
  );
};

const MenuItem = ({ children, onClick, testid, className = '' }) => (
  <button data-testid={testid} onClick={onClick} className={`w-full flex items-center px-3 py-2 text-xs hover:bg-secondary ${className}`}>{children}</button>
);
